import _ from 'lodash';
import { STAFF_PATHS } from '../../../../_configuration';
import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { generatePath, useHistory } from 'react-router';
import Select from 'react-select';
import aOrAn from 'indefinite';

// Material UI
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Checkbox from '@material-ui/core/Checkbox';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

// Data
import { numToTime } from '../../../lib/helpers/conversion.helpers';

// Components
import { PageHeaderRow, Spacer } from '../../_core/_ui/structure.components';
import { PrimaryWhiteButton, SecondaryButton, PrimaryButton } from '../../_core/_ui/buttons.component';
import { FormInput, IFormOption } from '../../_core/_ui/forms.component';
import { CHECK_IN_SORT_TYPES, JerseyIcon, carriedJersey, getPlayerRankingsInfo, getTeamBalanceInfo } from './check-in.helpers';
import { Bold } from '../../_core/_ui/typography.component';
import { ITeam } from './check-in.interface';
import JerseyAssignment from './jersey-assignment.component';
import { RsvpBadge } from '../../Attendance/rsvp-badge.component';
import { buildRsvpLookup, buildRsvpResponseLookup, getRsvpReasonText } from '../../Attendance/attendance.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { getPlayerName } from '../../../lib/utils/player-name.service';

interface ICheckIn {
  session?: IAssessmentSession;
  loading: boolean;
  fetchError: boolean;
  numTeams: number;
  setNumTeams: Function;
  checkedInPlayersHash: { [key: string]: IPlayerAssessment };
  teams: ITeam[];
  jerseys: { [key: string]: IJersey };
  selectedPlayer: IPlayerAssessment | null;
  setSelectedPlayer: Function;
  playerOptions: IFormOption[];
  positionOptions: IFormOption[];
  selectLoading: boolean;
  validateJersey: Function;
  assignJersey: Function;
  removeCheckedInPlayer: Function;
  changeTeam: Function;
  canEvaluate: boolean;
  removePlayer: Function;
  addPlayer: Function;
  changePosition: Function;
  setSortType: Function;
  retainJerseyAssignments: boolean;
  companyUseJerseyNotifications?: boolean;
  onSendJerseyNotification?: (playerAssessmentId: string, sendNotification: boolean) => Promise<any>;
  member?: IMember;
  showRankingsView: boolean;
  setShowRankingsView: Function;
  rebalanceTeams: Function;
  resetJerseys: Function;
}

export const CheckIn: React.FC<ICheckIn> = ({
  session,
  loading,
  fetchError,
  numTeams,
  setNumTeams,
  checkedInPlayersHash,
  teams,
  jerseys,
  selectedPlayer,
  setSelectedPlayer,
  playerOptions,
  positionOptions,
  selectLoading,
  validateJersey,
  assignJersey,
  removeCheckedInPlayer,
  changeTeam,
  canEvaluate,
  addPlayer,
  removePlayer,
  changePosition,
  setSortType,
  retainJerseyAssignments,
  companyUseJerseyNotifications,
  onSendJerseyNotification,
  member,
  showRankingsView,
  setShowRankingsView,
  rebalanceTeams,
  resetJerseys
}) => {
  const termLookup = useCompanySportTerminology()
  const athletesTerm = pluralTerm(termLookup[SportTerminology.ATHLETE]);

  const history = useHistory();
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  // Built before the early returns to keep hook order stable; Apollo's stable
  // array on cache hits means the memo re-builds only when the RSVP list changes.
  const isAttendanceRequested = Boolean(session?.isAttendanceRequested);
  const getRsvpStatus = useMemo(
    () => buildRsvpLookup(session?.attendanceResponses),
    [session?.attendanceResponses],
  );
  const getRsvpResponse = useMemo(
    () => buildRsvpResponseLookup(session?.attendanceResponses),
    [session?.attendanceResponses],
  );

  if (loading && !session) return <Typography>Loading...</Typography>;
  if (!session) return <Typography>Unable to check in {athletesTerm.toLowerCase()} at this time. Please try again later</Typography>;

  return (
    <div>
      <Box display="flex">
        <Typography variant="h4">{session.location} - {session.area}</Typography>

        <Spacer />
        <SecondaryButton
          startIcon={<ArrowBackIosIcon />}
          onClick={() => history.push('/upcoming')}
        >
          Home
        </SecondaryButton>
        {canEvaluate && <Box ml={2}><PrimaryWhiteButton onClick={() => history.push(generatePath(STAFF_PATHS.EVALUATE, { assessmentSessionId: session._id }))}>Evaluate {athletesTerm}</PrimaryWhiteButton></Box>}
      </Box>
      <PageHeaderRow><Typography variant="h5" gutterBottom>{session.address}</Typography></PageHeaderRow>
      <Typography variant="subtitle1">{session.ageGroup.name} | {dayjs(session.date).format('MMMM Do')} @ {numToTime(session.start)} | {session.playerAssessments?.length || 0} {athletesTerm}</Typography>

      <Box my={2}><Divider /></Box>

      <Box display="flex" alignItems="center" mb={2}>
        <Typography>Number of teams:</Typography>
        <Box ml={2}>
          <FormInput
            type="number"
            defaultValue={numTeams}
            min={1}
            max={session?.playerAssessments?.length || 1}
            onChange={(e: any) => setNumTeams(e.target.value)}
            onKeyUp={(e: any) => setNumTeams(e.currentTarget.value)}
          />
        </Box>
        <Box mx={2}>|</Box>
        <Box width="300px">
          <Select
            loading={selectLoading && !playerOptions.length}
            options={playerOptions}
            onChange={((option) => option && addPlayer(option.value))}
            placeholder={`Add ${aOrAn(termLookup[SportTerminology.ATHLETE])} to this assessment...`}
          />
        </Box>
        <Box mx={2}>|</Box>
        <Typography>Sort by:</Typography>
        <Box width="140px" ml={2}>
          <Select
            loading={selectLoading && !playerOptions.length}
            options={[{ value: CHECK_IN_SORT_TYPES.JERSEY, label: 'Jersey' }, { value: CHECK_IN_SORT_TYPES.LAST_NAME, label: 'Last Name' }]}
            onChange={((option) => option && setSortType(option.value))}
            placeholder="Jersey"
          />
        </Box>
        <Box mx={2}>|</Box>
        <Box>
          <PrimaryWhiteButton onClick={() => rebalanceTeams()}>
            Rebalance Teams
          </PrimaryWhiteButton>
        </Box>
        <Box mx={2}>|</Box>
        <Box>
          <PrimaryWhiteButton onClick={() => setResetConfirmOpen(true)}>
            Reset Jerseys
          </PrimaryWhiteButton>
        </Box>
        {member?.user?.isAdmin && (
          <>
            <Box mx={2}>|</Box>
            <Checkbox
              checked={showRankingsView}
              onChange={(e) => setShowRankingsView(e.target.checked)}
            />
            <Typography>Show Rankings</Typography>
          </>
        )}
      </Box>

      {!isAttendanceRequested && (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            RSVPs not requested for this {termLookup[SportTerminology.SESSION].toLowerCase()}.
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {teams.map((team, teamIndex) => (
          // @ts-ignore
          <Grid item xs={Math.floor(12 / numTeams)}>
            <Paper>
              <Box p={2}>
                <Box display="flex" alignItems="center">
                  <Typography variant="h6" gutterBottom>
                    Team {teamIndex + 1}
                    {showRankingsView && (() => {
                      const balanceInfo = getTeamBalanceInfo(team, session?.playerAssessments || []);
                      return balanceInfo.playerCount > 0 ? (
                        <Typography component="span" variant="body2" color="textSecondary" style={{ marginLeft: '8px', fontWeight: 'normal' }}>
                          ({balanceInfo.playerCount} players, avg rank: {balanceInfo.averageOverallRank})
                        </Typography>
                      ) : null;
                    })()}
                  </Typography>
                  <Spacer />

                  {_.map(team.playersByPosition, ((playerAssessments, positionName) => (
                    <Box key={positionName} textAlign="right" ml={2}>
                      <Typography><Bold>{positionName}</Bold></Typography>
                      <Typography><Bold>{playerAssessments.length}</Bold></Typography>
                    </Box>
                  )))}
                </Box>
                {_.map(team.playersByPosition, ((playerAssessments, positionName) => (
                  <Box key={positionName} mb={2}>
                    <Table size="small" style={{ border: '1px solid #ddd' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="subtitle2">{positionName}</Typography>
                          </TableCell>
                          {isAttendanceRequested && (
                            <TableCell>
                              <Typography variant="subtitle2" color="textSecondary">RSVP</Typography>
                            </TableCell>
                          )}
                          <TableCell padding="checkbox" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {playerAssessments.map((playerAssessment) => {
                          const isCheckedIn = Boolean(checkedInPlayersHash[playerAssessment._id]);
                          const rsvpStatus = getRsvpStatus(playerAssessment._id);
                          const isMuted = isAttendanceRequested && rsvpStatus === 'not_attending';
                          const toggleCheckIn = () => isCheckedIn ? removeCheckedInPlayer(playerAssessment._id) : setSelectedPlayer(playerAssessment);
                          const carried = retainJerseyAssignments ? carriedJersey(playerAssessment) : null;

                          return (
                            <React.Fragment key={playerAssessment._id}>
                              <TableRow style={{ opacity: isMuted ? 0.5 : 1 }}>
                                <TableCell padding="checkbox" style={{ cursor: 'pointer' }}>
                                  <Checkbox
                                    edge="start"
                                    disableRipple
                                    checked={isCheckedIn}
                                    onClick={toggleCheckIn}
                                  />
                                </TableCell>
                                <TableCell padding="none" style={{ cursor: 'pointer', paddingRight: '16px' }}>
                                  <JerseyIcon color={isCheckedIn ? jerseys[playerAssessment._id]?.color?.toLowerCase() : carried?.color?.toLowerCase()}>
                                    {isCheckedIn ? jerseys[playerAssessment._id]?.number : carried?.number}
                                  </JerseyIcon>
                                </TableCell>
                                <TableCell style={{ cursor: 'pointer' }} onClick={toggleCheckIn}>
                                  <Typography variant="body2">{getPlayerName(playerAssessment)}</Typography>
                                  {showRankingsView && (() => {
                                    const debugInfo = getPlayerRankingsInfo(playerAssessment, session?.playerAssessments || []);
                                    return (
                                      <Typography variant="caption" color="textSecondary">
                                        {`Pos Rank: ${debugInfo.positionRank}/${debugInfo.totalPositionPlayers} | Overall: ${debugInfo.overallRank}/${debugInfo.totalPlayers} | Score: ${debugInfo.adjustedScore.toFixed(1)}`}
                                      </Typography>
                                    );
                                  })()}
                                </TableCell>
                                {isAttendanceRequested && (
                                  <TableCell>
                                    <RsvpBadge status={rsvpStatus} />
                                    {getRsvpReasonText(getRsvpResponse(playerAssessment._id)) && (
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        {getRsvpReasonText(getRsvpResponse(playerAssessment._id))}
                                      </Typography>
                                    )}
                                  </TableCell>
                                )}
                                <TableCell padding="checkbox" align="right">
                                  <IconButton onClick={() => removePlayer(playerAssessment, teamIndex)}>
                                    <DeleteIcon/>
                                  </IconButton>
                                </TableCell>
                              </TableRow>

                              {selectedPlayer?._id === playerAssessment._id && (
                                <Dialog open={Boolean(selectedPlayer)} onClose={() => setSelectedPlayer(null)}
                                        aria-labelledby="form-dialog-title">
                                  <JerseyAssignment
                                    changeTeam={changeTeam}
                                    teams={teams}
                                    teamIndex={teamIndex}
                                    playerAssessment={selectedPlayer}
                                    onCancel={() => setSelectedPlayer(null)}
                                    checkedInPlayersHash={checkedInPlayersHash}
                                    positionOptions={positionOptions}
                                    changePosition={changePosition}
                                    validateJersey={validateJersey}
                                    selectLoading={selectLoading}
                                    assignJersey={assignJersey}
                                    jersey={jerseys[playerAssessment._id]}
                                    retainJerseyAssignments={retainJerseyAssignments}
                                    companyUseJerseyNotifications={companyUseJerseyNotifications}
                                    onSendJerseyNotification={onSendJerseyNotification}
                                  />
                                </Dialog>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )))}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Reset Jerseys Confirmation Dialog */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset All Jerseys</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will clear all jersey colors and numbers but keep team assignments. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setResetConfirmOpen(false)}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={async () => {
              setResetConfirmOpen(false);
              await resetJerseys();
            }}
          >
            Reset Jerseys
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

